// Secure OpenAI service that uses backend proxy instead of direct API calls
// This keeps the API key secure on the server side
// PERMANENT URLs - Set in stone on 2025-10-14
import { getApiBaseUrl as getPermanentApiBaseUrl } from '../config/permanent-urls';
import { Contact } from '../components/ApplicationWizard';
import { createInsufficientTokensError, handleInsufficientTokensError } from '../utils/insufficientTokensHandler';

interface Education {
  degree: string | null
  field: string | null
  requirement: 'required' | 'desired' | 'both' | null
}

interface BenefitsStructured {
  healthInsurance: string[]
  retirement: string[]
  timeOff: string[]
  allowances: string[]
  memberships: string[]
  bonusesEquity: string[]
  other: string[]
}

interface JobDescriptionWarning {
  code: string
  message: string
  evidence?: string
}

interface OpenAIParseResponse {
  company: string | null
  position: string | null
  positionCore: string | null
  positionSpecialization: string[]
  salary: string | null
  location: string | null
  workType: 'remote' | 'hybrid' | 'on-site' | null
  employmentType: 'Full-time' | 'Part-time' | 'Contract' | 'Temporary' | 'Internship' | 'Volunteer' | 'Other' | null
  contactInfo: string | null
  recruiter: string | null
  jobLevel: string | null
  jobLevelNormalized: string | null
  jobLevelConfidence: number | null
  jobLevelSource: string | null
  jobSummary: string | null
  responsibilities: string[]
  requiredQualifications: string[]
  optionalQualifications: string[]
  additionalQualifications: string[]
  education: Education[]
  technologies: string[]
  benefits: string[]
  benefitsStructured: BenefitsStructured
  postingDate: string | null
  postingAge: string | null
  applicantsCount: number | null
  applicantsText: string | null
  contacts?: Contact[]
  warnings?: JobDescriptionWarning[]
}

interface BackendParseResponse {
  success: boolean
  data?: Record<string, unknown>
  error?: string
}

export class SecureOpenAIService {
  private static instance: SecureOpenAIService
  private baseUrl: string
  private token: string | null = null

  private constructor() {
    // Use backend API URL instead of direct OpenAI API
    // ALWAYS use permanent URLs (same as apiService) - these are the source of truth
    // ALWAYS connect to AWS - never localhost backend
    const apiBaseUrl = getPermanentApiBaseUrl();
    // API Gateway endpoint is at /api/v1/openai (permanent-urls.ts includes /api/v1 already)
    this.baseUrl = `${apiBaseUrl}/openai`
    
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('token')
  }

  public static getInstance(): SecureOpenAIService {
    if (!SecureOpenAIService.instance) {
      SecureOpenAIService.instance = new SecureOpenAIService()
    }
    return SecureOpenAIService.instance
  }

  private createHeaders(extra?: HeadersInit, includeJson: boolean = true): Headers {
    const headers = new Headers(extra);
    
    if (includeJson) {
      headers.set('Content-Type', 'application/json');
    }

    // Check both instance token and localStorage (in case token was updated after instantiation)
    const token = this.token || localStorage.getItem('token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return headers;
  }

  public async parseJobDescription(description: string): Promise<OpenAIParseResponse> {
    // NO MOCK FALLBACKS - Always use real API or fail with clear error
    // Only use mock in explicit test mode
    if (process.env.NODE_ENV === 'test') {
      console.warn('🧪 TEST MODE: Using mock OpenAI service')
      return this.parseJobDescriptionMock(description)
    }

    // Require authentication token for production API calls
    // Check both instance token and localStorage (in case token was updated after instantiation)
    const token = this.token || localStorage.getItem('token');
    if (!token) {
      const error = 'Authentication required. Please log in to use the job description parser.'
      console.error('❌ OpenAI service: No authentication token available')
      throw new Error(error)
    }

    try {
      const response = await fetch(`${this.baseUrl}/parse-job-description/async`, {
        method: 'POST',
        headers: this.createHeaders(),
        body: JSON.stringify({
          job_description: description,
          async: true
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const tokenError = createInsufficientTokensError(errorData, response.status)
        if (tokenError) {
          handleInsufficientTokensError(tokenError)
          throw tokenError
        }
        const errorMessage = errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`
        throw new Error(errorMessage)
      }

      const result: BackendParseResponse = await response.json()

      if (!result.success || !result.data) {
        const errorMessage = result.error || 'Failed to parse job description - no data returned'
        console.error('❌ OpenAI service returned error:', errorMessage)
        throw new Error(errorMessage)
      }

      const jobId = (result.data as Record<string, unknown>).job_id as string | undefined
      if (jobId) {
        return await this.pollParseJob(jobId)
      }

      // Check if backend returned mock/fallback data (has "error" or "mock" in data)
      const dataStr = JSON.stringify(result.data)
      if (dataStr.includes('mock') || dataStr.includes('Mock') || dataStr.includes('fallback') || dataStr.includes('Fallback')) {
        const errorMessage = 'OpenAI service is using fallback/mock data. Please check backend configuration.'
        console.error('❌ OpenAI service returned mock/fallback data')
        throw new Error(errorMessage)
      }

      // Log success for debugging
      console.log('✅ OpenAI service successful, transforming data...')
      // Transform backend response to match frontend interface
      // Backend returns: { success: true, data: { parsed_data: {...}, ... } }
      // Pass the entire data object, transformBackendResponse will extract parsed_data
      return this.transformBackendResponse(result.data as Record<string, unknown>)

    } catch (error) {
      console.error('❌ Error parsing job description:', error)
      // Re-throw to let the UI handle the error properly
      throw error
    }
  }

  private async pollParseJob(jobId: string): Promise<OpenAIParseResponse> {
    const maxWaitMs = 15 * 60 * 1000
    const startTime = Date.now()
    let attempt = 0

    const getPollDelayMs = (attemptNumber: number): number => {
      // Fast initial polling, then back off to reduce load.
      const scheduleMs = [2000, 2000, 2000, 3000, 3000, 5000, 8000, 13000, 15000]
      const base = scheduleMs[Math.min(attemptNumber, scheduleMs.length - 1)]
      const jitter = 0.2
      const jitterFactor = 1 + (Math.random() * 2 - 1) * jitter
      return Math.max(500, Math.floor(base * jitterFactor))
    }

    while (true) {
      const response = await fetch(`${this.baseUrl}/parse-job-description/${jobId}`, {
        method: 'GET',
        headers: this.createHeaders()
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const errorMessage = errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`
        throw new Error(errorMessage)
      }

      const result: BackendParseResponse = await response.json()
      if (!result.success || !result.data) {
        const errorMessage = result.error || 'Failed to parse job description - no data returned'
        throw new Error(errorMessage)
      }

      const data = result.data as Record<string, unknown>
      const status = data.status as string | undefined

      if (status === 'succeeded') {
        return this.transformBackendResponse(data)
      }

      if (status === 'failed') {
        const errorMessage = (data.error as string | undefined) || 'Failed to parse job description'
        throw new Error(errorMessage)
      }

      if (Date.now() - startTime > maxWaitMs) {
        throw new Error('Parsing is taking longer than expected. Please try again in a moment.')
      }

      attempt += 1
      await new Promise(resolve => setTimeout(resolve, getPollDelayMs(attempt)))
    }
  }

  private transformBackendResponse(data: Record<string, unknown>): OpenAIParseResponse {
    // Transform the backend response to match the frontend interface
    // Backend returns snake_case (job_title, company_name) but frontend expects camelCase (position, company)
    // Also handle the nested parsed_data structure from backend
    // Extract parsed_data if nested (backend returns { parsed_data: {...} })
    const parsedData = (data.parsed_data as Record<string, unknown>) || data

    const normalizeWarnings = (): JobDescriptionWarning[] => {
      const raw = (parsedData.warnings as unknown) ?? (data.warnings as unknown)
      if (!Array.isArray(raw)) return []

      return raw
        .map((warning: any) => {
          if (!warning || typeof warning !== 'object') return null
          const code = typeof warning.code === 'string' ? warning.code : 'submission_instruction'
          const message = typeof warning.message === 'string' ? warning.message : ''
          const evidence = typeof warning.evidence === 'string' ? warning.evidence : undefined
          if (!message) return null
          return { code, message, evidence } as JobDescriptionWarning
        })
        .filter((warning): warning is JobDescriptionWarning => warning !== null)
    }
    
    // Map backend field names (snake_case) to frontend field names (camelCase)
    const getString = (key: string, fallback: string[] = []): string | null => {
      // Try camelCase first, then snake_case
      for (const k of [key, ...fallback]) {
        const value = parsedData[k]
        if (typeof value === 'string' && value.trim()) return value
      }
      return null
    }
    
    const getArray = (key: string, fallback: string[] = []): string[] => {
      // Try camelCase first, then snake_case
      for (const k of [key, ...fallback]) {
        const value = parsedData[k]
        if (Array.isArray(value) && value.length > 0) return value as string[]
      }
      return []
    }
    
    const jobTitle = getString('job_title', ['position', 'jobTitle'])
    const companyName = getString('company_name', ['company'])
    const salaryRange = getString('salary_range', ['salary'])
    const employmentTypeValue = getString('employment_type', ['employmentType'])
    const experienceLevel = getString('experience_level', ['jobLevel', 'experienceLevel'])
    const skillsRequired = getArray('skills_required', ['technologies', 'skills', 'requiredQualifications'])
    const benefitsArray = getArray('benefits', ['benefits'])
    const jobSummaryValue = getString('job_description_summary', ['jobSummary', 'summary', 'description'])
    
    // Extract position core from job title
    const positionCore = jobTitle ? this.extractPositionCore(jobTitle) : null
    const positionSpecialization = jobTitle ? this.extractPositionSpecialization(jobTitle) : []
    
    // Parse work type from location or description
    let workType: 'remote' | 'hybrid' | 'on-site' | null = null
    const locationValue = getString('location', ['location'])
    if (locationValue) {
      const lower = locationValue.toLowerCase()
      if (lower.includes('remote') && lower.includes('hybrid')) {
        workType = 'hybrid'
      } else if (lower.includes('remote')) {
        workType = 'remote'
      } else if (lower.includes('on-site') || lower.includes('onsite')) {
        workType = 'on-site'
      }
    }
    
    // Normalize employment type
    let employmentType: 'Full-time' | 'Part-time' | 'Contract' | 'Temporary' | 'Internship' | 'Volunteer' | 'Other' | null = null
    if (employmentTypeValue) {
      const lower = employmentTypeValue.toLowerCase()
      if (lower.includes('full-time') || lower.includes('full time')) {
        employmentType = 'Full-time'
      } else if (lower.includes('part-time') || lower.includes('part time')) {
        employmentType = 'Part-time'
      } else if (lower.includes('contract')) {
        employmentType = 'Contract'
      } else if (lower.includes('temporary')) {
        employmentType = 'Temporary'
      } else if (lower.includes('internship')) {
        employmentType = 'Internship'
      } else if (lower.includes('volunteer')) {
        employmentType = 'Volunteer'
      } else {
        employmentType = 'Other'
      }
    }
    
    // Extract education requirements - handle both array format and string format
    const educationArray = getArray('education', ['education'])
    const educationRequirementString = getString('education_requirement', ['educationRequirement'])
    const education: Education[] = []
    
    // First, process education array if provided
    if (educationArray && educationArray.length > 0) {
      educationArray.forEach(eduItem => {
        if (typeof eduItem === 'string') {
          // Parse education string (e.g., "Bachelor's degree in Computer Science")
          const lowerEdu = eduItem.toLowerCase()
          let degree: string | null = null
          let field: string | null = null
          
          // Extract degree type
          if (lowerEdu.includes("bachelor")) degree = "Bachelor's"
          else if (lowerEdu.includes("master")) degree = "Master's"
          else if (lowerEdu.includes("phd") || lowerEdu.includes("doctorate")) degree = "PhD"
          else if (lowerEdu.includes("associate")) degree = "Associate's"
          else if (lowerEdu.includes("certificate") || lowerEdu.includes("certification")) degree = "Certificate"
          
          // Extract field of study
          const inMatch = eduItem.match(/(?:in|of)\s+([^.]+)/i)
          if (inMatch) {
            field = inMatch[1].trim()
          } else {
            // Try to extract field after degree
            const afterDegree = eduItem.replace(/bachelor|master|phd|doctorate|associate|certificate/gi, '').trim()
            if (afterDegree && !afterDegree.toLowerCase().includes('degree')) {
              field = afterDegree
            }
          }
          
          education.push({
            degree: degree || eduItem,
            field: field,
            requirement: 'required'
          })
        }
      })
    }
    
    // Fallback to string format if array not provided
    if (education.length === 0 && educationRequirementString) {
      education.push({
        degree: educationRequirementString,
        field: null,
        requirement: 'required'
      })
    }
    
    // Build structured benefits from array
    const benefitsStructured = this.parseStructuredBenefits(benefitsArray)
    
    // Extract contacts from parsed data
    const extractContacts = (): Contact[] => {
      // Try to get contacts from parsedData (backend returns snake_case)
      const contactsData = parsedData.contacts || parsedData.contacts_array
      
      if (!contactsData || !Array.isArray(contactsData) || contactsData.length === 0) {
        return []
      }
      
      // Transform each contact from backend format (snake_case) to frontend format (camelCase)
      return contactsData.map((contact: any) => {
        if (!contact || typeof contact !== 'object') {
          return null
        }
        
        // Extract fields, handling both snake_case and camelCase
        const name = contact.name || contact.Name || null
        const title = contact.title || contact.Title || null
        const email = contact.email || contact.Email || null
        const phone = contact.phone || contact.Phone || null
        const linkedinUrl = contact.linkedin_url || contact.linkedinUrl || contact.LinkedInUrl || null
        const contactType = (contact.contact_type || contact.contactType || contact.ContactType || 'other') as Contact['contactType']
        const notes = contact.notes || contact.Notes || null
        
        // Ensure contactType is valid
        const validContactTypes: Contact['contactType'][] = ['job_poster', 'recruiter', 'hiring_manager', 'interviewer', 'other']
        const validContactType = validContactTypes.includes(contactType) ? contactType : 'other'
        
        // Return transformed contact
        return {
          name: name,
          title: title,
          email: email,
          phone: phone,
          linkedinUrl: linkedinUrl,
          contactType: validContactType,
          notes: notes,
          extracted: true // Mark as extracted from job description
        } as Contact
      }).filter((contact): contact is Contact => contact !== null)
    }
    
    const extractedContacts = extractContacts()
    
    // Debug logging for contacts extraction
    if (extractedContacts.length > 0) {
      console.log('✅ Extracted contacts:', extractedContacts.length, extractedContacts)
    } else {
      console.log('ℹ️ No contacts found in parsed data')
    }
    
    const result: OpenAIParseResponse = {
      company: companyName,
      position: jobTitle,
      positionCore: positionCore,
      positionSpecialization: positionSpecialization,
      salary: salaryRange,
      location: locationValue,
      workType: workType,
      employmentType: employmentType,
      contactInfo: getString('contact_info', ['contactInfo', 'contact']),
      recruiter: getString('recruiter', ['recruiter']),
      jobLevel: experienceLevel,
      jobLevelNormalized: experienceLevel ? this.normalizeJobLevel(experienceLevel) : null,
      jobLevelConfidence: experienceLevel ? 0.8 : null,
      jobLevelSource: experienceLevel ? 'extracted' : null,
      jobSummary: jobSummaryValue,
      responsibilities: getArray('responsibilities', ['responsibilities']),
      requiredQualifications: getArray('required_qualifications', ['requiredQualifications', 'required_qualifications', 'requirements', 'skills_required']),
      optionalQualifications: getArray('optional_qualifications', ['optionalQualifications', 'optional_qualifications', 'preferred_qualifications']),
      additionalQualifications: [],
      education: education,
      technologies: skillsRequired, // Map skills_required to technologies
      benefits: benefitsArray,
      benefitsStructured: benefitsStructured,
      postingDate: getString('posting_date', ['postingDate']),
      postingAge: getString('posting_age', ['postingAge']),
      applicantsCount: (() => {
        if (typeof parsedData.applicants_count === 'number') return parsedData.applicants_count
        if (typeof parsedData.applicantsCount === 'number') return parsedData.applicantsCount
        return null
      })(),
      applicantsText: getString('applicants_text', ['applicantsText', 'applicants']),
      contacts: extractedContacts,
      warnings: normalizeWarnings()
    }
    
    return result
  }

  // Mock implementation for development/testing
  public async parseJobDescriptionMock(description: string): Promise<OpenAIParseResponse> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000))

    // Extract basic information using simple regex patterns
    const company = this.extractCompany(description)
    const position = this.extractPosition(description)
    const salary = this.extractSalary(description)
    const location = this.extractLocation(description)
    const workType = this.extractWorkType(description)
    const employmentType = this.extractEmploymentType(description)
    const jobLevel = this.extractJobLevel(description)
    const technologies = this.extractTechnologies(description)
    const benefits = this.extractBenefits(description)
    const requiredQualifications = this.extractRequiredQualifications(description)
    const optionalQualifications = this.extractOptionalQualifications(description)
    const responsibilities = this.extractResponsibilities(description)
    const education = this.extractEducation(description)
    const benefitsStructured = this.parseStructuredBenefits(benefits)

    return {
      company,
      position,
      positionCore: this.extractPositionCore(position),
      positionSpecialization: this.extractPositionSpecialization(position),
      salary,
      location,
      workType,
      employmentType,
      contactInfo: this.extractContactInfo(description),
      recruiter: this.extractRecruiter(description),
      jobLevel,
      jobLevelNormalized: this.normalizeJobLevel(jobLevel),
      jobLevelConfidence: jobLevel ? 0.8 : null,
      jobLevelSource: jobLevel ? 'extracted' : null,
      jobSummary: this.extractJobSummary(description),
      responsibilities,
      requiredQualifications,
      optionalQualifications,
      additionalQualifications: [],
      education,
      technologies,
      benefits,
      benefitsStructured,
      postingDate: this.extractPostingDate(description),
      postingAge: this.extractPostingAge(description),
      applicantsCount: this.extractApplicantsCount(description),
      applicantsText: this.extractApplicantsText(description),
      warnings: []
    }
  }

  // Helper methods for data extraction
  private extractCompany(description: string): string | null {
    const patterns = [
      /(?:at|@|Company:|Employer:)\s*([A-Z][a-zA-Z\s&.,-]+?)(?:\s+Location|\s+Salary|\s+Full-time|\s+Part-time|\s+Contract|\s+Remote|\s+Hybrid|\s+On-site|\n|$)/i,
      /([A-Z][a-zA-Z\s&.,-]+?)\s+(?:is looking for|seeks|hiring)/i
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        let company = match[1].trim()
        // Clean up newlines and extra whitespace
        company = company.replace(/\s+/g, ' ').trim()
        // If it contains newlines, take only the first part
        if (company.includes('\n')) {
          company = company.split('\n')[0].trim()
        }
        return company
      }
    }
    return null
  }

  private extractPosition(description: string): string | null {
    const patterns = [
      /(?:Position:|Title:|Role:)\s*([^\n\r]+)/i,
      /(?:We are looking for|Seeking|Hiring)\s+([^\n\r]+?)(?:\s+to|$)/i,
      /^([^\n\r]+?)(?:\s+at\s+|\s+Location:)/i  // Match first line before "at" or "Location:"
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        let position = match[1].trim()
        // Clean up newlines and extra whitespace
        position = position.replace(/\s+/g, ' ').trim()
        // If it contains newlines, take only the first part
        if (position.includes('\n')) {
          position = position.split('\n')[0].trim()
        }
        return position
      }
    }
    return null
  }

  private extractPositionCore(position: string | null): string | null {
    if (!position) return null
    
    const coreRoles = [
      'Software Engineer', 'Developer', 'Programmer', 'Engineer',
      'Manager', 'Director', 'Analyst', 'Designer', 'Consultant',
      'Specialist', 'Coordinator', 'Administrator', 'Architect'
    ]
    
    for (const role of coreRoles) {
      if (position.toLowerCase().includes(role.toLowerCase())) {
        return role
      }
    }
    
    // If no core role found, extract first two words safely
    const words = position.split(' ')
    if (words.length >= 2) {
      return `${words[0]} ${words[1]}`
    } else if (words.length === 1) {
      return words[0]
    }
    
    return position
  }

  private extractPositionSpecialization(position: string | null): string[] {
    if (!position) return []
    
    const specializations = [
      'Full Stack', 'Frontend', 'Backend', 'Mobile', 'Web',
      'Senior', 'Junior', 'Lead', 'Principal', 'Staff',
      'React', 'Angular', 'Vue', 'Node.js', 'Python', 'Java',
      'DevOps', 'Cloud', 'AWS', 'Azure', 'GCP'
    ]
    
    const found: string[] = []
    for (const spec of specializations) {
      if (position.toLowerCase().includes(spec.toLowerCase())) {
        found.push(spec)
      }
    }
    
    return found
  }

  private extractSalary(description: string): string | null {
    const patterns = [
      /\$[\d,]{1,10}(?:\s*-\s*\$[\d,]{1,10})?/g,
      /(?:Salary:|Compensation:|Pay:)\s*([^\n\r]{1,100})/i
    ]
    
    for (const pattern of patterns) {
      const matches = description.match(pattern)
      if (matches && matches.length > 0) {
        return matches[0].trim()
      }
    }
    return null
  }

  private extractLocation(description: string): string | null {
    const patterns = [
      /(?:Location:|Based in:|Office:)\s*([^\n\r]+)/i,
      /(?:Remote|Hybrid|On-site)/i
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        return match[1].trim()
      }
    }
    return null
  }

  private extractWorkType(description: string): 'remote' | 'hybrid' | 'on-site' | null {
    const lowerDesc = description.toLowerCase()
    
    if (lowerDesc.includes('remote') && lowerDesc.includes('hybrid')) {
      return 'hybrid'
    } else if (lowerDesc.includes('remote')) {
      return 'remote'
    } else if (lowerDesc.includes('on-site') || lowerDesc.includes('onsite')) {
      return 'on-site'
    }
    
    return null
  }

  private extractEmploymentType(description: string): 'Full-time' | 'Part-time' | 'Contract' | 'Temporary' | 'Internship' | 'Volunteer' | 'Other' | null {
    const lowerDesc = description.toLowerCase()
    
    if (lowerDesc.includes('full-time') || lowerDesc.includes('full time')) {
      return 'Full-time'
    } else if (lowerDesc.includes('part-time') || lowerDesc.includes('part time')) {
      return 'Part-time'
    } else if (lowerDesc.includes('contract')) {
      return 'Contract'
    } else if (lowerDesc.includes('temporary')) {
      return 'Temporary'
    } else if (lowerDesc.includes('internship')) {
      return 'Internship'
    } else if (lowerDesc.includes('volunteer')) {
      return 'Volunteer'
    }
    
    return null
  }

  private extractJobLevel(description: string): string | null {
    const patterns = [
      /(?:Senior|Lead|Principal|Staff|Director|Manager)/i,
      /(?:Junior|Entry|Associate)/i,
      /(?:Mid-level|Mid|Intermediate)/i
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match) {
        return match[0].trim()
      }
    }
    return null
  }

  private normalizeJobLevel(jobLevel: string | null): string | null {
    if (!jobLevel) return null
    
    const lower = jobLevel.toLowerCase()
    
    if (lower.includes('senior') || lower.includes('lead') || lower.includes('principal') || lower.includes('staff')) {
      return 'Senior'
    } else if (lower.includes('junior') || lower.includes('entry') || lower.includes('associate')) {
      return 'Entry'
    } else if (lower.includes('mid') || lower.includes('intermediate')) {
      return 'Mid'
    } else if (lower.includes('director') || lower.includes('manager')) {
      return 'Management'
    }
    
    return jobLevel
  }

  private extractTechnologies(description: string): string[] {
    if (!description || description.trim().length === 0) {
      return []
    }
    
    const techPatterns = [
      /\b(?:React|Angular|Vue|JavaScript|TypeScript|Node\.js|Python|Java|C\+\+|C#|Go|Rust|PHP|Ruby|Swift|Kotlin)\b/gi,
      /\b(?:AWS|Azure|GCP|Docker|Kubernetes|MongoDB|PostgreSQL|MySQL|Redis|Elasticsearch)\b/gi,
      /\b(?:Git|Jenkins|CI\/CD|Agile|Scrum|DevOps|Microservices)\b/gi
    ]
    
    const technologies: string[] = []
    
    for (const pattern of techPatterns) {
      const matches = description.match(pattern)
      if (matches) {
        technologies.push(...matches.map(m => m.trim()))
      }
    }
    
    return [...new Set(technologies)] // Remove duplicates
  }

  private extractBenefits(description: string): string[] {
    const benefitPatterns = [
      /(?:Health insurance|Medical|Dental|Vision)/gi,
      /(?:401k|Retirement|Pension)/gi,
      /(?:PTO|Vacation|Sick leave|Holidays)/gi,
      /(?:Flexible schedule|Work-life balance)/gi,
      /(?:Stock options|Equity|Bonus)/gi
    ]
    
    const benefits: string[] = []
    
    for (const pattern of benefitPatterns) {
      const matches = description.match(pattern)
      if (matches) {
        benefits.push(...matches.map(m => m.trim()))
      }
    }
    
    return [...new Set(benefits)]
  }

  private parseStructuredBenefits(benefits: string[]): BenefitsStructured {
    const structured: BenefitsStructured = {
      healthInsurance: [],
      retirement: [],
      timeOff: [],
      allowances: [],
      memberships: [],
      bonusesEquity: [],
      other: []
    }
    
    for (const benefit of benefits) {
      const lower = benefit.toLowerCase()
      
      if (lower.includes('health') || lower.includes('medical') || lower.includes('dental') || lower.includes('vision')) {
        structured.healthInsurance.push(benefit)
      } else if (lower.includes('401k') || lower.includes('retirement') || lower.includes('pension')) {
        structured.retirement.push(benefit)
      } else if (lower.includes('pto') || lower.includes('vacation') || lower.includes('sick') || lower.includes('holiday')) {
        structured.timeOff.push(benefit)
      } else if (lower.includes('stock') || lower.includes('equity') || lower.includes('bonus')) {
        structured.bonusesEquity.push(benefit)
      } else {
        structured.other.push(benefit)
      }
    }
    
    return structured
  }

  private extractRequiredQualifications(description: string): string[] {
    const patterns = [
      /(?:Required|Must have|Requirements:)\s*([^\n\r]+)/gi,
      /(?:Experience with|Knowledge of)\s*([^\n\r]+)/gi
    ]
    
    const qualifications: string[] = []
    
    for (const pattern of patterns) {
      const matches = description.match(pattern)
      if (matches) {
        qualifications.push(...matches.map(m => m.trim()))
      }
    }
    
    return qualifications.slice(0, 5) // Limit to 5 items
  }

  private extractOptionalQualifications(description: string): string[] {
    const patterns = [
      /(?:Preferred|Nice to have|Bonus points|Plus:)\s*([^\n\r]+)/gi
    ]
    
    const qualifications: string[] = []
    
    for (const pattern of patterns) {
      const matches = description.match(pattern)
      if (matches) {
        qualifications.push(...matches.map(m => m.trim()))
      }
    }
    
    return qualifications.slice(0, 3) // Limit to 3 items
  }

  private extractResponsibilities(description: string): string[] {
    const patterns = [
      /(?:Responsibilities|Duties|Tasks:)\s*([^\n\r]+)/gi,
      /(?:You will|You'll)\s*([^\n\r]+)/gi
    ]
    
    const responsibilities: string[] = []
    
    for (const pattern of patterns) {
      const matches = description.match(pattern)
      if (matches) {
        responsibilities.push(...matches.map(m => m.trim()))
      }
    }
    
    return responsibilities.slice(0, 5) // Limit to 5 items
  }

  private extractEducation(description: string): Education[] {
    const patterns = [
      /(?:Bachelor|Master|PhD|Degree)\s+(?:in|of)\s+([^\n\r]+)/gi
    ]
    
    const education: Education[] = []
    
    for (const pattern of patterns) {
      const matches = description.match(pattern)
      if (matches) {
        education.push({
          degree: matches[0].trim(),
          field: matches[1]?.trim() || null,
          requirement: 'required'
        })
      }
    }
    
    return education.slice(0, 2) // Limit to 2 items
  }

  private extractContactInfo(description: string): string | null {
    const patterns = [
      /(?:Contact:|Email:|Phone:)\s*([^\n\r]+)/i,
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        return match[1].trim()
      }
    }
    return null
  }

  private extractRecruiter(description: string): string | null {
    const patterns = [
      /(?:Recruiter:|Hiring Manager:|Contact:)\s*([A-Za-z\s]+)/i
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        return match[1].trim()
      }
    }
    return null
  }

  private extractJobSummary(description: string): string | null {
    // Extract first 2-3 sentences as summary
    const sentences = description.split(/[.!?]+/).slice(0, 3)
    const summary = sentences.join('. ').trim()
    
    return summary.length > 50 ? summary + '.' : null
  }

  private extractPostingDate(description: string): string | null {
    const patterns = [
      /(?:Posted|Date:)\s*(\d{4}-\d{2}-\d{2})/i,
      /(\d{1,2}\/\d{1,2}\/\d{4})/i
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        return match[1].trim()
      }
    }
    return null
  }

  private extractPostingAge(description: string): string | null {
    const patterns = [
      /(?:Posted|Posted on)\s+(\d+\s+(?:days?|weeks?|months?)\s+ago)/i
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        return match[1].trim()
      }
    }
    return null
  }

  private extractApplicantsCount(description: string): number | null {
    const patterns = [
      /(\d+)\s+(?:applicants?|candidates?)/i
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        return parseInt(match[1])
      }
    }
    return null
  }

  private extractApplicantsText(description: string): string | null {
    const patterns = [
      /(\d+\s+(?:applicants?|candidates?))/i
    ]
    
    for (const pattern of patterns) {
      const match = description.match(pattern)
      if (match && match[1]) {
        return match[1].trim()
      }
    }
    return null
  }

  // Token management methods
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('token', token);
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem('token');
  }

  getToken(): string | null {
    return this.token;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }
}

export const secureOpenAIService = SecureOpenAIService.getInstance()
